import React, { useState, useMemo } from "react";
import {
  AlertCircle,
  Search,
  RefreshCw,
  BarChart3,
  X,
  Users,
} from "lucide-react";
import TimeTrackingFilters from "./TimeTracking/TimeTrackingFilters";
import TimeTrackingStatistics from "./TimeTracking/TimeTrackingStatistics";
import TimeTrackingTable from "./TimeTracking/TimeTrackingTable";
import TeamPerformanceAnalysis from "./TimeTracking/TeamPerformanceAnalysis";
import useTimeTrackingData from "../hooks/useTimeTrackingData";
import useFilterOptions from "../hooks/useFilterOptions";
import { usePermissions } from "../../hooks/usePermissions";
import { getJiraKeyParts } from "./TimeTracking/utils/sortingUtils";
import { PERMISSIONS } from "../../constants/permissions";
import isEqual from "lodash/isEqual";
import { formatHours } from "./TimeTracking/utils/roundUtils";
// Helpers (outside component for stable refs + perf)
const toArray = (value) => (Array.isArray(value) ? value : []);

const getRemovedItems = (prev = [], next = []) =>
  prev.filter((item) => !next.includes(item));

const buildUserProjectMap = (users = []) => {
  const map = new Map();
  users.forEach((user) => {
    map.set(user.id, toArray(user.projects));
  });
  return map;
};

const filterUsersByRemainingProjects = (
  userIds = [],
  userProjectMap,
  remainingProjectSet
) => {
  return userIds.filter((userId) => {
    const userProjects = userProjectMap.get(userId);

    // If user not found OR global (no project scoping), keep them.
    if (!userProjects || userProjects.length === 0) return true;

    return userProjects.some((projectId) =>
      remainingProjectSet.has(projectId)
    );
  });
};

const SESSION_KEY = 'timetracking_date_range';

// Module-level cache: survives Dashboard remounts within the same session.
// Cleared on full page reload, which is what we want.
const dashboardCache = {
  filters: null,
  appliedFilters: null,
  grouping: null,
  sorting: null,
  activeView: null,
  hasInitialized: false,
};

const TimeTrackingApp = ({ onClose, activeJiraAccount }) => {

  const { can } = usePermissions();
  const canSeeTeamPerformance = can(PERMISSIONS.REPORTS);

  const getLocalTodayStr = () => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  };



  const getInitialDateRange = () => {
    const today = getLocalTodayStr();
    return { startDate: today, endDate: today };
  };

  const { startDate: initialStart, endDate: initialEnd } = getInitialDateRange();

  const defaultFilters = {
    worklogAuthor: [],
    issueAssignee: [],
    project: [],
    status: [],
    priority: [],
    sprint: [],
    startDate: initialStart,
    endDate: initialEnd,
    minHours: "",
    maxHours: "",
  };

  const defaultGrouping = { field: "none", expanded: {} };
  const defaultSorting = {
    groupLevel: { field: "totalHours", direction: "desc" },
    withinGroup: { field: "loggedDate", direction: "desc" },
  };

  const [filters, setFilters] = useState(
    () => dashboardCache.filters ?? defaultFilters
  );
  const [appliedFilters, setAppliedFilters] = useState(
    () => dashboardCache.appliedFilters ?? defaultFilters
  );
  const [grouping, setGrouping] = useState(
    () => dashboardCache.grouping ?? defaultGrouping
  );
  const [sorting, setSorting] = useState(
    () => dashboardCache.sorting ?? defaultSorting
  );
  const [activeView, setActiveView] = useState(
    () => dashboardCache.activeView ?? "table"
  );
  const [hasApplied, setHasApplied] = useState(false);


  // Sync state changes back into the module cache so a remount restores them.
  React.useEffect(() => {
    dashboardCache.filters = filters;
    dashboardCache.appliedFilters = appliedFilters;
    dashboardCache.grouping = grouping;
    dashboardCache.sorting = sorting;
    dashboardCache.activeView = activeView;
  }, [filters, appliedFilters, grouping, sorting, activeView]);

  const accountId = activeJiraAccount?.id ?? null;

  // Clear cached state when account changes so we don't leak data across accounts.
  const prevAccountIdRef = React.useRef(accountId);
  React.useEffect(() => {
    if (prevAccountIdRef.current !== null && prevAccountIdRef.current !== accountId) {
      dashboardCache.filters = null;
      dashboardCache.appliedFilters = null;
      dashboardCache.grouping = null;
      dashboardCache.sorting = null;
      dashboardCache.activeView = null;
      dashboardCache.hasInitialized = false;
      setFilters(defaultFilters);
      setAppliedFilters(defaultFilters);
      setGrouping(defaultGrouping);
      setSorting(defaultSorting);
      setActiveView("table");
      setHasApplied(false);
    }
    prevAccountIdRef.current = accountId;
  }, [accountId]);

  const handleSortingChange = (newSorting) => {
    setSorting(newSorting);
  };

  const {
    allWorkLogs,
    loading,
    error,
    hasLoadedData,
    loadWorklogData,
    progress,
    isProgressVisible,
    cancelWorklogRequest,
    isCancelling,
    hasActiveProgress,
  } = useTimeTrackingData(appliedFilters, accountId, hasApplied);
  const selectedProjectKeys = useMemo(
    () => Array.isArray(filters.project) ? filters.project : [],
    [filters.project]
  );
  const { filterOptions } = useFilterOptions(activeJiraAccount, selectedProjectKeys);

  // Calculate filtered work logs
  const filteredWorkLogs = useMemo(() => {
    let filtered = [...allWorkLogs];

    if (grouping.field === 'none') {
      filtered.sort((a, b) => {
        const { field, direction } = sorting.withinGroup;
        let aVal = a[field];
        let bVal = b[field];

        // Handle date/datetime fields
        if ((field === 'loggedDate' || field === 'started') && aVal && bVal) {
          aVal = new Date(aVal).getTime();
          bVal = new Date(bVal).getTime();
        }

        // Handle Jira issue keys specially
        if (field === 'issue_key') {
          const aParts = getJiraKeyParts(aVal);
          const bParts = getJiraKeyParts(bVal);

          let cmp;
          if (aParts.prefix !== bParts.prefix) {
            cmp = aParts.prefix > bParts.prefix ? 1 : -1;
          } else {
            cmp = aParts.num - bParts.num;
          }

          return direction === 'asc' ? cmp : -cmp;
        }

        // Handle null/undefined values
        if (aVal == null) aVal = '';
        if (bVal == null) bVal = '';

        if (direction === "asc") {
          return aVal > bVal ? 1 : -1;
        } else {
          return aVal < bVal ? 1 : -1;
        }
      });
    }

    return filtered;
  }, [allWorkLogs, sorting, grouping.field]);

  // Calculate enhanced statistics with proper unique ticket calculations
  const statistics = useMemo(() => {
    if (filteredWorkLogs.length === 0) return null;

    const totalLogged = filteredWorkLogs.reduce(
      (sum, log) => sum + log.loggedHours,
      0
    );

    // Calculate unique tickets and their estimates properly
    const uniqueIssues = new Map();

    filteredWorkLogs.forEach(log => {
      if (!uniqueIssues.has(log.issue_key)) {
        uniqueIssues.set(log.issue_key, {
          key: log.issue_key,
          estimatedHours: log.estimatedHours,
          summary: log.issue_summary,
          status: log.issue_status,
          priority: log.issue_priority,
          loggedHours: 0,
          loggedEntries: 0
        });
      }

      const issue = uniqueIssues.get(log.issue_key);
      issue.loggedHours += log.loggedHours;
      issue.loggedEntries += 1;
    });

    // Calculate total estimated hours from unique issues only
    const totalEstimated = Array.from(uniqueIssues.values())
      .reduce((sum, issue) => sum + issue.estimatedHours, 0);

    const totalVariance = totalLogged - totalEstimated;
    const avgVariance = totalVariance / filteredWorkLogs.length;

    // Calculate author vs assignee statistics
    const samePersonLogs = filteredWorkLogs.filter(
      (log) => log.author === log.issue_assignee
    );
    const differentPersonLogs = filteredWorkLogs.filter(
      (log) => log.author !== log.issue_assignee
    );

    // Calculate unique counts
    const uniqueAuthors = new Set(filteredWorkLogs.map(log => log.author).filter(Boolean)).size;
    const uniqueAssignees = new Set(filteredWorkLogs.map(log => log.issue_assignee).filter(Boolean)).size;
    const uniqueProjects = new Set(filteredWorkLogs.map(log => log.issue_project_key)).size;
    const uniqueDates = new Set(filteredWorkLogs.map(log => log.loggedDate)).size;

    // Calculate efficiency metrics based on unique tickets
    const efficiency = totalEstimated > 0 ? ((totalLogged / totalEstimated) * 100) : 0;

    // Calculate estimation quality
    const issuesWithEstimates = Array.from(uniqueIssues.values()).filter(issue => issue.estimatedHours > 0).length;
    const issuesWithoutEstimates = Array.from(uniqueIssues.values()).filter(issue => issue.estimatedHours === 0).length;

    // Calculate work patterns
    const avgHoursPerDay = uniqueDates > 0 ? totalLogged / uniqueDates : 0;
    const avgHoursPerIssue = uniqueIssues.size > 0 ? totalLogged / uniqueIssues.size : 0;


    // console.log("worklogs: ", allWorkLogs);
    // console.log("filteredWorkLogs: ", filteredWorkLogs);

    return {
      totalEntries: filteredWorkLogs.length,
      totalLogged: formatHours(totalLogged),
      totalEstimated: totalEstimated.toFixed(1),
      avgVariance: avgVariance.toFixed(1),
      efficiency: efficiency.toFixed(1),

      // Author vs Assignee metrics
      samePersonLogs: samePersonLogs.length,
      differentPersonLogs: differentPersonLogs.length,
      samePersonPercentage: (
        (samePersonLogs.length / filteredWorkLogs.length) *
        100
      ).toFixed(1),

      // Unique counts
      uniqueAuthors,
      uniqueAssignees,
      uniqueIssues: uniqueIssues.size,
      uniqueProjects,
      workDays: uniqueDates,

      // Estimation quality
      issuesWithEstimates,
      issuesWithoutEstimates,
      estimationCoverage: uniqueIssues.size > 0 ?
        ((issuesWithEstimates / uniqueIssues.size) * 100).toFixed(1) : 0,

      // Work patterns
      avgHoursPerDay,
      avgHoursPerIssue,
      projectCount: uniqueProjects,

      // Variance based on unique tickets
      totalVarianceUnique: totalVariance.toFixed(1),
    };
  }, [filteredWorkLogs]);


  const getUpdatedFiltersOnProjectChange = (
    prevFilters,
    nextProjectSelection,
    filterOptions
  ) => {
    const prevProjects = toArray(prevFilters.project);
    const nextProjects = toArray(nextProjectSelection);

    const removedProjects = getRemovedItems(prevProjects, nextProjects);

    // If no project removed → only update project
    if (removedProjects.length === 0) {
      return {
        ...prevFilters,
        project: nextProjects,
      };
    }

    const remainingProjectSet = new Set(nextProjects);
    const userProjectMap = buildUserProjectMap(filterOptions?.users);

    return {
      ...prevFilters,
      project: nextProjects,
      issueAssignee: filterUsersByRemainingProjects(
        toArray(prevFilters.issueAssignee),
        userProjectMap,
        remainingProjectSet
      ),
      worklogAuthor: filterUsersByRemainingProjects(
        toArray(prevFilters.worklogAuthor),
        userProjectMap,
        remainingProjectSet
      ),
    };
  };
  // Check if filters have changed
  const filtersChanged = useMemo(() => {
    return !isEqual(filters, appliedFilters);
  }, [filters, appliedFilters]);

  // Event handlers
  const handleFilterChange = (key, value) => {
    if (key === "project") {
      setFilters((prev) => getUpdatedFiltersOnProjectChange(prev, value, filterOptions));
      return;
    }

    setFilters((prev) => ({ ...prev, [key]: value }));
  };

  const handleDateRangePreset = (startDate, endDate) => {
    setFilters((prev) => ({ ...prev, startDate, endDate }));
  };

  const handleGroupingChange = (field) => {
    setGrouping((prev) => ({ ...prev, field, expanded: {} }));
  };

  const applyFilters = () => {
    setHasApplied(true);
    setAppliedFilters({ ...filters });
  };

  const handleRefreshData = () => {
    // Force a fresh fetch, bypassing the module-level cache short-circuit.
    loadWorklogData();
  };

  const resetFilters = () => {
    try { sessionStorage.removeItem(SESSION_KEY); } catch { }
    const todayStr = getLocalTodayStr();
    const resetValues = {
      worklogAuthor: [],
      issueAssignee: [],
      project: [],
      status: [],
      priority: [],
      sprint: [],
      startDate: todayStr,
      endDate: todayStr,
      minHours: "",
      maxHours: "",
    };
    setFilters(resetValues);
    setAppliedFilters(resetValues);
    setHasApplied(false);
    dashboardCache.filters = resetValues;
    dashboardCache.appliedFilters = resetValues;
    dashboardCache.grouping = defaultGrouping;
    dashboardCache.sorting = defaultSorting;
    dashboardCache.activeView = "table";
  };

  // console.log('filterOptions', filterOptions);

  return (
    <div className="min-h-screen bg-slate-100 dark:bg-slate-900 transition-colors duration-300">
      {/* Header */}
      <div className="bg-white dark:bg-slate-800 shadow-sm border-b border-gray-200 dark:border-white/10">
        <div className="px-6 py-4">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-xl font-semibold text-gray-900 dark:text-slate-100">
                Time Tracking Dashboard
              </h1>
              <p className="text-sm text-gray-600 dark:text-slate-400">
                Analyze worklog data with proper unique ticket estimation and detailed descriptions
              </p>
            </div>
            {onClose && (
              <button
                onClick={onClose}
                className="p-2 text-gray-400 dark:text-slate-400 hover:text-gray-600 dark:hover:text-slate-200"
              >
                <X className="w-5 h-5" />
              </button>
            )}
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="px-6 py-6">
        {/* Filters */}
        <TimeTrackingFilters
          filters={filters}
          filterOptions={filterOptions}
          filtersChanged={filtersChanged}
          loading={loading}
          onFilterChange={handleFilterChange}
          onDateRangePreset={handleDateRangePreset}
          onApplyFilters={applyFilters}
          onResetFilters={resetFilters}
          onRefreshData={handleRefreshData}
        />

        {/* Progress */}
        {isProgressVisible && (
          <div className="bg-white dark:bg-slate-800 rounded-lg shadow-sm border border-blue-100 dark:border-blue-900/30 p-4 mb-6">
            <div className="flex items-center justify-between text-sm text-gray-600 dark:text-slate-400">
              <div>
                <span className="font-medium text-gray-800 dark:text-slate-200 block">
                  {progress.phase === "fetching_issues"
                    ? "Fetching Issues"
                    : "Processing Issues"}
                </span>
                {progress.phase === "fetching_issues" && (
                  <span className="text-xs text-gray-500 dark:text-slate-500">
                    {progress.message || "Contacting Jira..."}
                  </span>
                )}
              </div>
              <span className="font-mono">
                {progress.phase === "fetching_issues"
                  ? `${progress.current}`
                  : `${progress.current}/${progress.total || "?"}`}
              </span>
            </div>
            <div className="mt-3 h-3 bg-gray-100 dark:bg-slate-700 rounded-full overflow-hidden">
              <div
                className={`h-3 rounded-full transition-all duration-500 ${progress.status === "failed"
                  ? "bg-red-500"
                  : progress.status === "cancelled" || progress.status === "cancelling"
                    ? "bg-yellow-500"
                    : "bg-blue-600"
                  }`}
                style={{ width: `${progress.percentage || 0}%` }}
              ></div>
            </div>
            <div className="flex items-center justify-between mt-2 text-xs text-gray-500 dark:text-slate-500">
              <span>
                {progress.message ||
                  (progress.status === "completed"
                    ? "Completed"
                    : "Working...")}
              </span>
              <span>
                {progress.phase === "fetching_issues"
                  ? "..."
                  : `${(progress.percentage || 0).toFixed(1)}%`}
              </span>
            </div>
            {progress.issueKey && (
              <p className="text-xs text-gray-500 dark:text-slate-500 mt-1">
                Current issue:{" "}
                <span className="font-medium text-gray-700 dark:text-slate-300">
                  {progress.issueKey}
                </span>
              </p>
            )}
            {hasActiveProgress && (
              <button
                onClick={cancelWorklogRequest}
                disabled={
                  isCancelling ||
                  progress.status === "cancelling" ||
                  progress.status === "cancelled"
                }
                className="mt-3 inline-flex items-center px-3 py-1.5 text-xs font-medium text-red-600 dark:text-red-400 border border-red-200 dark:border-red-800/50 hover:bg-red-50 dark:hover:bg-red-900/30 rounded disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isCancelling || progress.status === "cancelling"
                  ? "Cancelling..."
                  : "Cancel Request"}
              </button>
            )}
          </div>
        )}

        {/* Load Data Button */}
        {!hasLoadedData && (
          <div className="bg-white dark:bg-slate-800 rounded-lg shadow-sm mb-6 p-6 text-center">
            <div className="max-w-md mx-auto">
              <BarChart3 className="w-12 h-12 mx-auto text-gray-400 dark:text-slate-500 mb-4" />
              <h3 className="text-lg font-medium text-gray-900 dark:text-slate-100 mb-2">
                Ready to Load Data
              </h3>
              <p className="text-sm text-gray-600 dark:text-slate-400 mb-4">
                Configure your filters above and click the "Apply Filters"
                button to load worklog data with comprehensive statistics and descriptions.
              </p>
              <button
                onClick={applyFilters}
                disabled={loading}
                className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center mx-auto"
              >
                {loading ? (
                  <>
                    <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                    Loading Data...
                  </>
                ) : (
                  <>
                    <Search className="w-4 h-4 mr-2" />
                    Load Data
                  </>
                )}
              </button>
            </div>
          </div>
        )}

        {/* Error Message */}
        {error && (
          <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800/50 rounded-lg p-4 mb-6">
            <div className="flex items-center">
              <AlertCircle className="w-5 h-5 text-red-500 mr-2" />
              <span className="text-sm text-red-700 dark:text-red-400">{error}</span>
            </div>
          </div>
        )}

        {/* Statistics - Pass filteredWorkLogs for proper unique ticket calculation */}
        <TimeTrackingStatistics
          statistics={statistics}
          filteredWorkLogs={filteredWorkLogs}
        />

        {/* View Toggle */}
        {hasLoadedData && (
          <div className="bg-white dark:bg-slate-800 rounded-lg shadow-sm mb-6 p-4">
            <div className="flex items-center space-x-2 border-b border-gray-200 dark:border-white/10">
              <button
                onClick={() => setActiveView("table")}
                className={`px-4 py-2 text-sm font-medium transition-colors ${activeView === "table"
                  ? "text-blue-600 dark:text-blue-400 border-b-2 border-blue-600 dark:border-blue-400"
                  : "text-gray-600 dark:text-slate-300 hover:text-gray-900 dark:hover:text-slate-100"
                  }`}
              >
                <BarChart3 className="w-4 h-4 inline mr-2" />
                Worklog Table
              </button>
              {canSeeTeamPerformance && (
                <button
                  onClick={() => setActiveView("team")}
                  className={`px-4 py-2 text-sm font-medium transition-colors ${activeView === "team"
                    ? "text-blue-600 dark:text-blue-400 border-b-2 border-blue-600 dark:border-blue-400"
                    : "text-gray-600 dark:text-slate-300 hover:text-gray-900 dark:hover:text-slate-100"
                    }`}
                >
                  <Users className="w-4 h-4 inline mr-2" />
                  Team Performance
                </button>
              )}
            </div>
          </div>
        )}

        {/* Conditional Rendering based on active view */}
        {(!canSeeTeamPerformance || activeView === "table") ? (
          <TimeTrackingTable
            filteredWorkLogs={filteredWorkLogs}
            grouping={grouping}
            sorting={sorting}
            hasLoadedData={hasLoadedData}
            onGroupingChange={handleGroupingChange}
            onSortingChange={handleSortingChange}
            onToggleGroupExpansion={(groupKey) =>
              setGrouping((prev) => ({
                ...prev,
                expanded: {
                  ...prev.expanded,
                  [groupKey]: !prev.expanded[groupKey],
                },
              }))
            }
          />
        ) : (
          <TeamPerformanceAnalysis
            startDate={appliedFilters.startDate}
            endDate={appliedFilters.endDate}
            filters={appliedFilters}
            worklogs={allWorkLogs}
            worklogsLoaded={hasLoadedData}
            accountId={accountId}
          />
        )}
      </div>
    </div>
  );
};

export default TimeTrackingApp;